import { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useCurrentPanelMountTargetId } from '@frontend/panel-engine';
import { theme } from '@frontend/theme';
import { Button, ModalControlResponse, Tray } from '@frontend/design-system';

const ScheduleRequestTrayWrapper = ({
  children,
  onClose,
  modalProps,
}: PropsWithChildren<{
  onClose: () => void;
  modalProps: ModalControlResponse;
}>) => {
  const mountTargetId = useCurrentPanelMountTargetId();

  return (
    <Tray
      width='medium'
      css={scheduleRequestTrayWrapperStyle}
      {...modalProps.modalProps}
      onClose={onClose}
      autoFocusTimeout={3600000 * 2}
      mountTarget={`#${mountTargetId}`}
      showBackdrop={false}
    >
      {children}
    </Tray>
  );
};

const ScheduleRequestTrayHeader = ({ children, onClose }: PropsWithChildren<{ onClose: () => void }>) => {
  const { t } = useTranslation('scheduleCalendarRequest');

  return (
    <Tray.Header
      css={scheduleRequestTrayHeaderStyle}
      Buttons={
        <Button
          variant='secondary'
          iconName='x'
          size='large'
          label={t('Close Schedule Request Modal')}
          onClick={onClose}
        />
      }
    >
      {children}
    </Tray.Header>
  );
};

const ScheduleRequestTrayContent = ({ children }: { children: React.ReactNode }) => {
  return <Tray.Body css={scheduleRequestTrayContentStyle}>{children}</Tray.Body>;
};

export const ScheduleRequestTray = Object.assign(ScheduleRequestTrayWrapper, {
  Header: ScheduleRequestTrayHeader,
  Content: ScheduleRequestTrayContent,
});

const scheduleRequestTrayWrapperStyle = css({
  padding: 0,
  gap: theme.spacing(0),
  borderWidth: '1px 0px 0px 1px',
  borderStyle: 'solid',
  borderColor: theme.colors.neutral20,
  width: '100%',
});

const scheduleRequestTrayHeaderStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(3),
});

const scheduleRequestTrayContentStyle = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});
